import { useCallback, useEffect, useRef } from "react";
import {
  FX_DEFAULT_PARAMS,
  normalizeFxNode,
  type FxNode,
  type FxType,
  type InstrumentSlot,
} from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import {
  clampFxHeight,
  FX_HEIGHT_MAX,
  FX_HEIGHT_MIN,
} from "../../lib/dockSizes";
import {
  defaultWetForFxType,
  formatFxParam,
  FX_PARAM_LABELS,
  FX_PARAM_OPTIONS,
  FX_PARAM_RANGES,
  FX_PARAM_TOGGLES,
  FX_TYPE_LABELS,
  FX_TYPES,
} from "../../lib/fxMeta";
import { useDockResize } from "../../lib/useDockResize";
import { downloadJson } from "../../lib/projectFile";
import { patchUi, uiStore } from "../../store/uiStore";
import { projectStore } from "../../store/projectStore";
import { LearnableControl } from "../LearnableControl";
import { Checkbox } from "../ui/Checkbox";
import { Slider } from "../ui/Slider";
import {
  dockFieldControl,
  dockPanel,
  dockPanelClose,
  dockPanelFooter,
  dockPanelFooterBtn,
  dockPanelHeader,
  dockPanelTitle,
  fxBottomPanel,
} from "./dockChrome";

let fxIdSeq = 0;

function newFxId(): string {
  fxIdSeq += 1;
  return `fx-${Date.now().toString(36)}-${fxIdSeq.toString(36)}`;
}

/** React list keys need a stable id; engine leaves `id` optional. */
function withFxId(fx: FxNode): FxNode {
  return fx.id ? fx : { ...fx, id: newFxId() };
}

function scopeToggleClass(active: boolean): string {
  return active
    ? "border-lab-accent bg-[rgba(61,139,253,0.22)] text-white"
    : "border-lab-line bg-transparent text-lab-muted hover:border-white/30 hover:text-slate-200";
}

/**
 * Bottom FX panel: master bus or current instrument insert stack.
 * Effect cards run left → right in a horizontal strip. Drag the top edge to resize.
 */
export function FxDock() {
  const open = useStoreSelector(uiStore, s => s.fxOpen);
  const storedHeight = useStoreSelector(uiStore, s => s.fxHeight);
  const bypass = useStoreSelector(uiStore, s => s.fxBypass);
  const scope = useStoreSelector(uiStore, s => s.fxScope);
  const instrumentSlot = useStoreSelector(uiStore, s => s.instrumentSlot);
  const masterFx = useStoreSelector(projectStore, s => s.fx);
  const slotFx = useStoreSelector(projectStore, s => s.slotFx);
  const panelRef = useRef<HTMLDivElement>(null);

  const commitHeight = useCallback((next: number) => {
    patchUi({ fxHeight: next });
    getLabActions()?.persistPanels({ fxHeight: next });
  }, []);

  const {
    size: height,
    dragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    nudge,
  } = useDockResize({
    storedSize: storedHeight,
    clamp: clampFxHeight,
    onCommit: commitHeight,
    axis: "y",
    edge: "start",
  });

  const trackSlot: InstrumentSlot = instrumentSlot ?? 0;
  const editingTrack = scope === "track";
  const effects = editingTrack ? (slotFx[trackSlot] ?? []) : masterFx;
  const title = "FX";
  const bypassApplies = !editingTrack;
  const learnFxPrefix = editingTrack
    ? `/track/${trackSlot}/fx`
    : "/master/fx";
  const learnBypassPath = bypassApplies ? "/master/fxBypass" : undefined;

  useEffect(() => {
    if (open) {
      panelRef.current?.focus();
    }
  }, [open]);

  function commit(next: FxNode[], status?: string): void {
    const normalized = next.map(fx => withFxId(normalizeFxNode(fx)));
    if (editingTrack) {
      getLabActions()?.applySlotFxStack(trackSlot, normalized);
    } else {
      getLabActions()?.applyFxStack(normalized);
    }
    if (status) {
      getLabActions()?.setStatus(status);
    }
  }

  function closeFx(): void {
    patchUi({ fxOpen: false });
    getLabActions()?.persistPanels({ fxOpen: false });
    getLabActions()?.setStatus("FX closed.");
  }

  function addEffect(type: FxType): void {
    commit(
      [...effects, normalizeFxNode({ type, wet: defaultWetForFxType(type) })],
      `Added ${FX_TYPE_LABELS[type]}`,
    );
  }

  function updateAt(index: number, patch: Partial<FxNode> | ((fx: FxNode) => FxNode)): void {
    const next = effects.map((fx, i) => {
      if (i !== index) {
        return fx;
      }
      return typeof patch === "function" ? patch(fx) : normalizeFxNode({ ...fx, ...patch });
    });
    commit(next);
  }

  function move(index: number, delta: number): void {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= effects.length) {
      return;
    }
    const next = effects.slice();
    const swap = next[index]!;
    next[index] = next[nextIndex]!;
    next[nextIndex] = swap;
    commit(next);
  }

  function stripFilename(): string {
    return editingTrack ? `track-${trackSlot + 1}-fx.adam.json` : "master-fx.adam.json";
  }

  function stripJson(): string {
    return JSON.stringify(effects, null, 2);
  }

  function applyStripJson(text: string, status: string): void {
    const parsed = JSON.parse(text) as unknown;
    if (!Array.isArray(parsed)) {
      throw new Error("Expected an array of FX nodes");
    }
    commit(
      parsed.map(fx => normalizeFxNode(fx as FxNode)),
      status,
    );
  }

  async function copyStrip(): Promise<void> {
    try {
      await navigator.clipboard.writeText(stripJson());
      getLabActions()?.setStatus("Copied FX strip");
    } catch {
      getLabActions()?.setStatus("Copy failed — clipboard permission denied");
    }
  }

  async function pasteStrip(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      applyStripJson(text, "Pasted FX strip");
    } catch (error) {
      getLabActions()?.setStatus(
        `Paste failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  function exportStrip(): void {
    downloadJson(stripJson(), stripFilename());
    getLabActions()?.setStatus(`Exported ${stripFilename()}`);
  }

  function importStrip(): void {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.adam.json,.json";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      try {
        applyStripJson(await file.text(), `Imported ${file.name}`);
      } catch (error) {
        getLabActions()?.setStatus(
          `Import failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    });
    input.click();
  }

  const keyedEffects = effects.map((fx, index) => ({
    fx,
    key: fx.id ?? `fx-index-${index}`,
  }));

  return (
    <section
      id="fx-dock"
      className={`fx-dock ${fxBottomPanel} ${open ? "flex" : "hidden"} ${
        dragging ? "select-none" : ""
      }`}
      style={{ height }}
    >
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize FX"
        aria-valuemin={FX_HEIGHT_MIN}
        aria-valuemax={FX_HEIGHT_MAX}
        aria-valuenow={height}
        tabIndex={0}
        className={[
          "absolute top-0 right-0 left-0 z-20 h-1.5 cursor-row-resize touch-none",
          "bg-transparent hover:bg-lab-accent/35 active:bg-lab-accent/50",
          dragging ? "bg-lab-accent/40" : "",
        ].join(" ")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={event => {
          const step = event.shiftKey ? 32 : 16;
          if (event.key === "ArrowUp") {
            event.preventDefault();
            nudge(step);
          } else if (event.key === "ArrowDown") {
            event.preventDefault();
            nudge(-step);
          }
        }}
      />
      <div
        ref={panelRef}
        className={`fx-panel ${dockPanel}`}
        tabIndex={0}
        role="region"
        aria-label={title}
      >
        <div className={`fx-panel-header ${dockPanelHeader}`}>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <div className={`fx-panel-title ${dockPanelTitle}`}>{title}</div>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${scopeToggleClass(!editingTrack)}`}
                aria-pressed={!editingTrack}
                onClick={() => getLabActions()?.setFxScope("master")}
              >
                Master
              </button>
              <button
                type="button"
                className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${scopeToggleClass(editingTrack)}`}
                aria-pressed={editingTrack}
                title="Edit insert FX for the current instrument track"
                onClick={() => getLabActions()?.setFxScope("track")}
              >
                Track I{trackSlot + 1}
              </button>
            </div>
            <select
              aria-label="Add effect"
              className={`${dockFieldControl} !w-auto min-w-[9rem] shrink-0 !py-0.5`}
              defaultValue=""
              onChange={event => {
                const type = event.target.value as FxType;
                if (!type) {
                  return;
                }
                addEffect(type);
                event.target.value = "";
              }}
            >
              <option value="">Add effect…</option>
              {FX_TYPES.map(type => (
                <option key={type} value={type}>
                  {FX_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {bypassApplies && (
              learnBypassPath ? (
                <LearnableControl path={learnBypassPath}>
                  <button
                    type="button"
                    title={bypass ? "FX stack bypassed — click to enable" : "Bypass FX stack"}
                    aria-label="Bypass master FX"
                    aria-pressed={bypass}
                    className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${
                      bypass
                        ? "border-amber-400/80 bg-amber-400 text-slate-900"
                        : "border-lab-line bg-transparent text-lab-muted hover:border-white/30 hover:text-slate-200"
                    }`}
                    onClick={() => getLabActions()?.setFxBypass(!bypass)}
                  >
                    Bypass
                  </button>
                </LearnableControl>
              ) : (
                <button
                  type="button"
                  title={bypass ? "FX stack bypassed — click to enable" : "Bypass FX stack"}
                  aria-label="Bypass master FX"
                  aria-pressed={bypass}
                  className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${
                    bypass
                      ? "border-amber-400/80 bg-amber-400 text-slate-900"
                      : "border-lab-line bg-transparent text-lab-muted hover:border-white/30 hover:text-slate-200"
                  }`}
                  onClick={() => getLabActions()?.setFxBypass(!bypass)}
                >
                  Bypass
                </button>
              )
            )}
            <button type="button" className={`fx-panel-close ${dockPanelClose}`} onClick={closeFx}>
              Close
            </button>
          </div>
        </div>

        <div
          className={`min-h-0 flex-1 overflow-x-auto overflow-y-hidden px-[0.55rem] py-[0.45rem]${
            bypassApplies && bypass ? " opacity-45" : ""
          }`}
        >
          {keyedEffects.length === 0 ? (
            <p className="muted m-0 flex h-full items-center text-[11px] leading-snug">
              {editingTrack
                ? "Empty insert — dry instrument. Add effects above."
                : "Empty stack — dry mix. Add effects above."}
            </p>
          ) : (
            <div className="flex h-full min-w-min flex-row items-stretch gap-[0.45rem]">
              {keyedEffects.map(({ fx, key }, index) => (
                <FxCard
                  key={key}
                  fx={fx}
                  index={index}
                  count={keyedEffects.length}
                  learnFxPrefix={learnFxPrefix}
                  onUpdate={patch => updateAt(index, patch)}
                  onMove={delta => move(index, delta)}
                  onRemove={() => {
                    commit(
                      effects.filter((_, i) => i !== index),
                      "Removed FX node",
                    );
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className={`fx-panel-footer ${dockPanelFooter}`}>
          <button
            type="button"
            className={dockPanelFooterBtn}
            title="Copy FX strip JSON to the clipboard"
            onClick={() => void copyStrip()}
          >
            Copy strip
          </button>
          <button
            type="button"
            className={dockPanelFooterBtn}
            title="Paste FX strip JSON from the clipboard"
            onClick={() => void pasteStrip()}
          >
            Paste strip
          </button>
          <button
            type="button"
            className={dockPanelFooterBtn}
            title="Download FX strip as a JSON file"
            onClick={exportStrip}
          >
            Export strip
          </button>
          <button
            type="button"
            className={dockPanelFooterBtn}
            title="Load FX strip from a JSON file"
            onClick={importStrip}
          >
            Import strip
          </button>
        </div>
      </div>
    </section>
  );
}

function FxCard({
  fx,
  index,
  count,
  learnFxPrefix,
  onUpdate,
  onMove,
  onRemove,
}: {
  fx: FxNode;
  index: number;
  count: number;
  learnFxPrefix?: string;
  onUpdate: (patch: Partial<FxNode> | ((node: FxNode) => FxNode)) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  const defaults = FX_DEFAULT_PARAMS[fx.type];
  const labels = FX_PARAM_LABELS[fx.type];
  const selectClass =
    "w-full rounded border border-lab-line bg-[#12161c] px-[0.3rem] py-0.5 text-slate-200 [font:inherit]";
  const enabledControl = (
    <Checkbox
      variant="power"
      checked={fx.enabled}
      title={fx.enabled ? "Disable effect" : "Enable effect"}
      aria-label={fx.enabled ? "Disable effect" : "Enable effect"}
      onCheckedChange={enabled => onUpdate({ enabled })}
    />
  );

  return (
    <section
      className={`flex h-full w-[13.5rem] shrink-0 flex-col overflow-y-auto rounded-md border border-lab-line bg-lab-panel-deep px-[0.45rem] pt-1.5 pb-2 ${fx.enabled ? "" : "opacity-55"}`}
    >
      <div className="mb-[0.35rem] grid grid-cols-[auto_1fr_auto] items-center gap-[0.35rem]">
        {learnFxPrefix ? (
          <LearnableControl path={`${learnFxPrefix}/${index}/enabled`}>
            {enabledControl}
          </LearnableControl>
        ) : (
          enabledControl
        )}
        <select
          className={selectClass}
          value={fx.type}
          onChange={event => {
            const nextType = event.target.value as FxType;
            onUpdate(
              normalizeFxNode({
                id: fx.id,
                type: nextType,
                enabled: fx.enabled,
                wet: fx.wet,
              }),
            );
          }}
        >
          {FX_TYPES.map(type => (
            <option key={type} value={type}>
              {FX_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
        <div className="flex gap-[0.15rem]">
          <button
            type="button"
            className="min-w-6 px-[0.35rem] py-[0.15rem] text-[11px]"
            disabled={index === 0}
            title="Move left"
            onClick={() => onMove(-1)}
          >
            ←
          </button>
          <button
            type="button"
            className="min-w-6 px-[0.35rem] py-[0.15rem] text-[11px]"
            disabled={index === count - 1}
            title="Move right"
            onClick={() => onMove(1)}
          >
            →
          </button>
          <button
            type="button"
            className="min-w-6 px-[0.35rem] py-[0.15rem] text-[11px]"
            title="Remove"
            onClick={onRemove}
          >
            ×
          </button>
        </div>
      </div>

      <label className="mt-0.5 grid grid-cols-[4.4rem_1fr_auto] items-center gap-1 text-[11px] text-lab-muted">
        <span>Wet</span>
        <Slider
          min={0}
          max={1}
          step={0.01}
          value={fx.wet}
          aria-label="Wet"
          learnPath={learnFxPrefix ? `${learnFxPrefix}/${index}/wet` : undefined}
          onChange={wet => onUpdate({ wet })}
        />
        <span className="min-w-[2.4rem] text-right tabular-nums text-[10px] text-slate-300">
          {fx.wet.toFixed(2)}
        </span>
      </label>

      {Object.keys(defaults).map(key => {
        const value = fx.params[key] ?? defaults[key]!;
        const paramPath = learnFxPrefix
          ? `${learnFxPrefix}/${index}/params/${key}`
          : undefined;
        if (FX_PARAM_TOGGLES.has(key)) {
          const toggle = (
            <Checkbox
              className="justify-self-start"
              checked={value >= 0.5}
              aria-label={labels[key] ?? key}
              onCheckedChange={on => {
                const v = on ? 1 : 0;
                onUpdate(node => ({
                  ...node,
                  params: { ...node.params, [key]: v },
                }));
              }}
            />
          );
          return (
            <label
              key={key}
              className="mt-0.5 grid grid-cols-[4.4rem_1fr] items-center gap-1 text-[11px] text-lab-muted"
            >
              <span>{labels[key] ?? key}</span>
              {paramPath ? (
                <LearnableControl path={paramPath}>{toggle}</LearnableControl>
              ) : (
                toggle
              )}
            </label>
          );
        }
        const options = FX_PARAM_OPTIONS[fx.type]?.[key];
        if (options) {
          const select = (
            <select
              className={selectClass}
              value={Math.round(value)}
              onChange={event => {
                const v = Number(event.target.value);
                onUpdate(node => ({
                  ...node,
                  params: { ...node.params, [key]: v },
                }));
              }}
            >
              {options.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
          return (
            <label
              key={key}
              className="mt-0.5 grid grid-cols-[4.4rem_1fr] items-center gap-1 text-[11px] text-lab-muted"
            >
              <span>{labels[key] ?? key}</span>
              {paramPath ? (
                <LearnableControl path={paramPath}>{select}</LearnableControl>
              ) : (
                select
              )}
            </label>
          );
        }
        const range = FX_PARAM_RANGES[key] ?? { min: 0, max: 1, step: 0.01 };
        const label = labels[key] ?? key;
        return (
          <label
            key={key}
            className="mt-0.5 grid grid-cols-[4.4rem_1fr_auto] items-center gap-1 text-[11px] text-lab-muted"
          >
            <span>{label}</span>
            <Slider
              min={range.min}
              max={range.max}
              step={range.step}
              value={value}
              aria-label={label}
              learnPath={paramPath}
              onChange={v => {
                onUpdate(node => ({
                  ...node,
                  params: { ...node.params, [key]: v },
                }));
              }}
            />
            <span className="min-w-[2.4rem] text-right tabular-nums text-[10px] text-slate-300">
              {formatFxParam(value)}
            </span>
          </label>
        );
      })}
    </section>
  );
}
